import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import draggable from 'vuedraggable'

import { Caption, Subtitle1, Subtitle2 } from '@src/elements/typography/typography'

import { Modal } from '@src/elements/modal/modal'
import { Icon } from '@src/elements/icon/icon'
import { Button } from '@src/elements/button/button'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Spacer } from '@src/elements/spacer/spacer'
import Station from '@src/data/station'
import WorkoutData from '@src/data/workout-data'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { StationService } from '@src/service/station'

import './station-dependent-exercises.sass'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ExercisePreview } from '@src/components/card/exercise-card/exercise-preview/exercise-preview'
import { exerciseName } from '@src/util/exercise-name'

@Component({
  components: {
    draggable: draggable
  },
  computed: {
    stations: {
      get: function () {
        return this.$store.state.stations
      },
      set: function (stations) {
        this.$store.state.stations = stations
      }
    },
    dragOptions () {
      return {
        group: 'selectedExercises',
        chosenClass: 'dragged-exercise'
      }
    },
    workout () {
      return this.$store.state.new_workout
    },
    exercises () {
      return this.$store.state.availableExercises
    }
  }
})
export class StationDependentExercises extends Vue {

  @Prop({ required: true })
  showModal: boolean

  @Prop({ required: true })
  saveCallback: () => void

  @Prop({ required: true })
  closeCallback: () => void

  private workout: WorkoutData
  private selectedExercises: WorkoutExercise[] = []
  private stations: Station[]

  private dragOptions: any
  private loading: boolean = true
  private exercises: ExerciseResponse[]

  constructor () {
    super()
  }

  created () {
    return this.loadStations().then(() => {
      this.loading = false
    })
  }

  loadStations () {
    return new StationService().getAll()
      .then(stations => this.$store.dispatch('setStations', stations))
      .then(() => {
        this.selectedExercises = this.$store.state.new_workout.workoutExercises.filter(workoutExercise => {
          // filter exercises which are not set to a station
          return this.stations.filter(station => station.exercises.includes(workoutExercise)).length === 0
        })
      })
  }

  render (h: CreateElement) {
    const selectedExercises = this.selectedExercises
      .map((workoutExercise) => {
        const allEquipmentItems = workoutExercise.exercise.equipment.map((equipmentName) => {
          return this.renderEquipment(h, equipmentName)
        })

        const currentExercise = {
          ...workoutExercise,
          exercise: this.exercises.find(availableExercise => availableExercise.id === workoutExercise.exercise.id) || workoutExercise.exercise
        }

        return <div class='ex'>
            {this.renderPreview(h ,currentExercise.exercise)}
            <div class='exercise-details'>
              <div class='exercise-header'>
               <Subtitle1>{exerciseName(this, currentExercise.exercise)}</Subtitle1>
                <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.MODAL.DRAG_MESSAGE')}>
                  <Icon name={'util/drag'} />
                </Tooltip>
              </div>
             <div class='equipmentWrapper'>{allEquipmentItems}</div>
            </div>
        </div>
      })
    let sortedStations = this.stations.slice().sort(function (a,b) {
      return a.position - b.position
    })

    const stations = sortedStations.map((station) => {
      let stationIndex = this.stations.indexOf(station)
      let stationExercises = station.exercises.map((workoutExercise,index) => {

        const allEquipmentItems = workoutExercise.exercise.equipment.map((equipmentName) => {
          return this.renderEquipment(h, equipmentName)
        })
        let currentExercise = {
          ...workoutExercise,
          exercise: this.exercises.find(availableExercise => availableExercise.id === workoutExercise.exercise.id) || workoutExercise.exercise
        }
        return <div class='ex' key={station.id + index}>
          {this.renderPreview(h ,currentExercise.exercise)}
          <div class='exercise-details'>
            <div class='exercise-header'>
              {currentExercise.exercise && <Subtitle1> {exerciseName(this, currentExercise.exercise)} </Subtitle1>}
              <div class='delete-ex' onClick={() => this.removeFromStation(stationIndex, currentExercise)}><Icon
                name={'util/minus'} /></div>
            </div>
            <div class='equipmentWrapper'>{allEquipmentItems}</div>
          </div>
        </div>
      })
      const allStationEquipment = station.equipment.map((equipmentName) => {
        return this.renderEquipment(h, equipmentName)
      })

      return <div class='station'>
        <div class='station-content'>
          <div class='station-header'>
            <div class='station-names'>
              <Subtitle1>{station.displayName}</Subtitle1>
              <Subtitle2>{station.name}</Subtitle2>
            </div>
              <Caption>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.POSITION')} {station.position + 1}</Caption>
          </div>
          <div class='equipmentWrapper-header'>{allStationEquipment}</div>
          {stationExercises.length === 0 ?
            // a v-model on a loop variable compiles to this.$set inside the ES5 map callback, where this is undefined
            <draggable
              class='exercises-droppable'
              value={station.exercises}
              onInput={(exercises) => this.setStationExercises(station, exercises)}
              options={{
                ...this.dragOptions,
                delay: 5000
              }}
            >
              <div class='empty-list-message'>
                <Subtitle1> {this.$t('PAGE.CONTENT.WORKOUT.NO_EXERCISES_ASSIGNED')}</Subtitle1>
                <Spacer/>
                <div draggable='false' >{this.$t('PAGE.CONTENT.WORKOUT.DRAG_DROP_EXERCISES')}</div>
              </div>
            </draggable> :
            // a v-model on a loop variable compiles to this.$set inside the ES5 map callback, where this is undefined
            <draggable class='exercises' value={station.exercises}
                       onInput={(exercises) => this.setStationExercises(station, exercises)}
                       options={{ ...this.dragOptions, delay: 0 }}>{stationExercises}</draggable>
          }
        </div>
      </div>
    })

    return <Modal overflow={'hidden'} show={this.showModal} title={this.$t('PAGE.CONTENT.WORKOUT.MODAL.TITLE')}
                  cssClass='save-options'
                  closeCallback={this.closeCallback}>
      {this.workout.stationLocked ?
        <div class='move-exercises'>
          {this.loading ? <Loader /> :
            <div class='draggable-lists-wrapper'>
              <div class='exercise-list'>
                <Subtitle1 class='title'>{this.$t('PAGE.CONTENT.WORKOUT.MODAL.EXERCISES')}</Subtitle1>
                <draggable class='exercise-list-scrollable' v-model={this.selectedExercises}
                           options={this.dragOptions}>{selectedExercises}</draggable>
              </div>

              <div class='station-list'>
                <Subtitle1 class='title'>{this.$t('PAGE.CONTENT.WORKOUT.MODAL.STATIONS')}</Subtitle1>
                <div class='station-list-scrollable'>
                  {stations}
                </div>
              </div>
            </div>
          }
        </div> : ''}

      <div slot='footer' class='btn-row'>
        <Button class='cancel-btn' type='outline' align='right' label={this.$t('CANCEL')} click={this.closeCallback} />
        <Button type='elevated' class='save-btn' align='right' label={this.$t('SAVE')}
                disabled={this.selectedExercises.length !== 0} click={() => this.save()} />
      </div>
    </Modal>
  }

  removeFromStation (stationIndex, exercise) {
    let exerciseIndex = this.stations[stationIndex].exercises.findIndex(
      (ex) => ex.exercise.id === exercise.exercise.id
    )
    if (exerciseIndex !== -1) {
      this.stations[stationIndex].exercises.splice(exerciseIndex, 1)
    }

    this.selectedExercises.push(exercise)
  }

  setStationExercises (station: Station, exercises: WorkoutExercise[]) {
    this.$set(station, 'exercises', exercises)
  }

  save () {
    // if needed, remove exercises - stations setup
    if (!this.workout.stationLocked) {
      this.stations.forEach(station => station.exercises = [])
    }

    // proceed to saving
    this.saveCallback()
    this.closeCallback()
  }

  renderEquipment (h: CreateElement, equipment: string) {
    return <div class='equipment'>
      <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipment.toUpperCase())}>
        <Icon name={'equipment/' + equipment} />
      </Tooltip>
    </div>
  }

  renderPreview (h: CreateElement,exercise) {
    return <div slot='preview'>
      <ExercisePreview exercise={exercise} />
    </div>
  }
}
